import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePreview } from "../contexts/PreviewContext";
import { detectSurface } from "../utils/surfaceDetection";
import { safeCliCallback, safePreviewRedirect, safeReturnTo } from "../utils/loginTargets";
import {
  AlertCircle, GitBranch, PenTool, Terminal, BookOpen, Zap, Code, Rocket,
  ClipboardList, MessagesSquare,
} from "lucide-react";

/**
 * Sign-in is one button.
 *
 * Atlas Identity is the only way a human authenticates to Cortex: there is no
 * password form, no magic link and no Google button here any more. Pressing
 * "Sign in" leaves for `/api/auth/atlas/start`, which is a full-page navigation
 * rather than a fetch — the API is the confidential OIDC client and the whole
 * handshake happens there.
 *
 * Everything else on this page is the CLI / preview-server flow, and it is
 * unchanged. The important invariant it carries: approving a `cortex login` is
 * ALWAYS an explicit button press, never automatic, so a signed-in user who
 * opens a crafted `?session=` link does not hand their session to whoever made
 * it.
 */

/** What the API's callback can tell us went wrong.
 *
 * Atlas deliberately tells the browser very little — the real reason
 * (`admission_refused`, `domain_upstream_mismatch`, …) goes to
 * `identity_audit_log`, which nothing in Cortex renders. So these have to be
 * actionable without being specific, and every one of them names a next step.
 */
const ERROR_MESSAGES: Record<string, string> = {
  access_denied:
    "Joveo Identity didn't allow this sign-in. If you think you should have access, contact your Cortex administrator.",
  no_account:
    "No Cortex account found for that email. Please accept your invite first, or ask your administrator for access.",
  deactivated:
    "That account has been deactivated. Contact your Cortex administrator if you think this is wrong.",
  invalid_token:
    "We couldn't verify that sign-in. Please try again.",
  exchange_failed:
    "We couldn't complete the sign-in with Joveo Identity. Please try again.",
  expired:
    "That sign-in attempt expired. Please try again.",
  bad_response:
    "Joveo Identity returned an unexpected response. Please try again.",
  misconfigured:
    "Cortex isn't configured correctly for Joveo Identity. Please contact your administrator.",
  sign_in_failed: "Sign-in failed. Please try again.",
};

/** Enable and Docs are partner-facing: their users have never heard of Cortex
 * or "Joveo Identity", and their administrator is whoever invited them. Codes
 * not listed here carry no product name and fall through to ERROR_MESSAGES. */
function externalErrorMessages(product: string, admin: string): Record<string, string> {
  return {
    ...ERROR_MESSAGES,
    access_denied:
      `This sign-in wasn't allowed. If you think you should have access, contact ${admin}.`,
    no_account:
      `That email doesn't have access to ${product}. Use the email your invite was sent to, or ask ${admin} to invite you.`,
    deactivated:
      `That account has been deactivated. Contact ${admin} if you think this is wrong.`,
    exchange_failed:
      "We couldn't complete the sign-in. Please try again.",
    bad_response:
      "Sign-in returned an unexpected response. Please try again.",
    misconfigured:
      `${product} sign-in isn't set up correctly. Please contact ${admin}.`,
  };
}

const EXTERNAL_SIGN_IN = {
  // A dark wordmark: knocked out to white on the indigo panel, no rounding.
  logo: "/joveo-logo.png",
  panelLogoClass: "h-8 brightness-0 invert opacity-90",
  mobileLogoClass: "h-9",
  button: "Continue with email",
  subtitle: "Sign in with your email address",
  note: "Next, enter your email. We'll send you a one-time code, or take you to your company's sign-in.",
} as const;

/** Product name and wording for the sign-in card, by surface. */
const SIGN_IN_COPY = {
  cortex: {
    product: "Cortex",
    logo: "/cortex-logo.png",
    // A square app icon.
    panelLogoClass: "h-9 rounded-lg object-cover drop-shadow-md",
    mobileLogoClass: "h-10 rounded-xl object-cover shadow-sm",
    label: "Cortex",
    errors: ERROR_MESSAGES,
    button: "Sign in with Joveo",
    subtitle: "Continue with your Joveo account",
    note: "Cortex uses Joveo Identity for sign-in. You'll be redirected to verify who you are, then brought straight back.",
    footer: "Need access? Ask your Cortex administrator for an invite.",
    panelFooter: "Joveo Engineering",
  },
  enable: {
    ...EXTERNAL_SIGN_IN,
    product: "Joveo Enable",
    label: "Enable",
    errors: externalErrorMessages("Joveo Enable", "your workspace admin"),
    footer: "Need access? Ask your workspace admin to invite you.",
    panelFooter: "Joveo",
  },
  docs: {
    ...EXTERNAL_SIGN_IN,
    product: "Joveo Docs",
    label: "Docs",
    errors: externalErrorMessages("Joveo Docs", "your Joveo contact"),
    footer: "Need access? Ask your Joveo contact for an invite.",
    panelFooter: "Joveo Developer Portal",
  },
} as const;

const SURFACE_COPY = {
  studio: {
    headline: "Documentation, versioned like code.",
    features: [
      { icon: PenTool, title: "Visual editing", desc: "Write and structure docs without touching markup" },
      { icon: GitBranch, title: "Branch and merge", desc: "Draft on a branch, review, then publish" },
      { icon: Terminal, title: "CLI and preview", desc: "Edit on disk and preview locally" },
    ],
  },
  docs: {
    headline: "Everything you need to build with Joveo.",
    features: [
      { icon: BookOpen, title: "Guides and reference", desc: "Walkthroughs alongside full API reference" },
      { icon: Code, title: "Live API explorer", desc: "Try endpoints against real responses" },
      { icon: Zap, title: "Always current", desc: "Published straight from source" },
    ],
  },
  enable: {
    headline: "Onboarding, without the back and forth.",
    features: [
      { icon: Rocket, title: "Guided setup", desc: "Every step of go-live in one place" },
      { icon: ClipboardList, title: "Clear requests", desc: "Know exactly what's needed and when" },
      { icon: MessagesSquare, title: "Talk to your team", desc: "Questions answered in context" },
    ],
  },
  preview: {
    headline: "Your workspace, rendered live.",
    features: [
      { icon: GitBranch, title: "Branch management", desc: "Switch, compare, and merge branches locally" },
      { icon: Zap, title: "Fast iteration", desc: "Edit files on disk, see changes instantly" },
      { icon: Code, title: "Full editor access", desc: "Visual editor, source mode, and diff views" },
    ],
  },
} as const;

function BrandPanel({ variant, copy = SIGN_IN_COPY.cortex }: {
  variant: keyof typeof SURFACE_COPY;
  copy?: (typeof SIGN_IN_COPY)[keyof typeof SIGN_IN_COPY];
}) {
  const { headline, features } = SURFACE_COPY[variant];
  return (
    <div
      className="hidden lg:flex lg:w-[480px] xl:w-[520px] shrink-0 flex-col justify-between p-10 relative overflow-hidden"
      style={{ background: "linear-gradient(145deg, #312e81 0%, #4338ca 50%, #6366f1 100%)" }}
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[400px] h-[400px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #818cf8, transparent 70%)" }}
      />
      <div className="relative z-10">
        <div className="flex items-center gap-2.5 mb-1">
          <img src={copy.logo} alt="" className={copy.panelLogoClass} />
          <span className="text-[22px] font-semibold text-white/90 tracking-[0.02em] select-none">{copy.label}</span>
        </div>
      </div>
      <div className="relative z-10 space-y-6">
        <p className="text-[28px] leading-[1.3] font-semibold text-white/95 max-w-[360px]">{headline}</p>
        <div className="space-y-4">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="mt-0.5 size-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <Icon className="size-3.5 text-indigo-200" />
              </div>
              <div>
                <div className="text-[13px] font-medium text-white/90">{title}</div>
                <div className="text-[12px] text-indigo-200/70 mt-0.5">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="relative z-10">
        <p className="text-[11px] text-indigo-300/50">{copy.panelFooter}</p>
      </div>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-2.5 text-sm text-slate-400">
        <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        {label}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLocalPreview, previewConfigLoaded } = usePreview();
  // Only the CLI / preview server on this machine may receive tokens (see loginTargets).
  const callbackUrl = safeCliCallback(searchParams.get("callback"));
  const cliSessionId = searchParams.get("session");
  const sessionSource = searchParams.get("source"); // "cli" or "preview"
  const surface = useMemo(() => detectSurface(), []);
  const isStudio = !surface.isDedicatedSurface;
  const copy =
    surface.appSlug === "enable" ? SIGN_IN_COPY.enable
      : surface.appSlug === "docs" ? SIGN_IN_COPY.docs
        : SIGN_IN_COPY.cortex;
  const [error, setError] = useState<string | null>(
    copy.errors[searchParams.get("error") ?? ""] ?? null
  );
  const [cliApproved, setCliApproved] = useState(false);
  // A CLI/preview sign-in request waiting for the user's explicit OK.
  const [cliConfirmPending, setCliConfirmPending] = useState(false);
  const callbackSentRef = useRef(false);

  const brandVariant: keyof typeof SURFACE_COPY = isStudio
    ? "studio"
    : surface.appSlug === "enable"
      ? "enable"
      : "docs";

  // Send tokens to callback URL via POST, then redirect browser to the origin
  const sendToCallback = useCallback(async () => {
    if (!callbackUrl || callbackSentRef.current) return;
    callbackSentRef.current = true;
    const token = localStorage.getItem("cortex_access_token") || "";
    const refreshToken = localStorage.getItem("cortex_refresh_token") || "";
    if (!token) { callbackSentRef.current = false; return; }
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(callbackUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: token, refresh_token: refreshToken, email: "" }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`callback ${res.status}`);
      // Check if this is a preview server callback or CLI callback
      const cbUrl = new URL(callbackUrl);
      if (cbUrl.pathname === "/preview/callback") {
        // Preview server — redirect as GET to set localStorage and go to studio
        window.location.href = callbackUrl;
      } else {
        // CLI login — our own success screen (never write the local server's
        // response into this page).
        setCliApproved(true);
      }
    } catch {
      // Callback failed (CLI server not running, timeout, etc.) — navigate normally
      callbackSentRef.current = false;
      const returnTo = safeReturnTo(searchParams.get("returnTo"));
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else if (surface.isDedicatedSurface && surface.appSlug) {
        navigate(`/${surface.appSlug}`, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [callbackUrl, surface, navigate, searchParams]);

  // Approve CLI device session after login (if ?session= param is present)
  const approveCLISession = useCallback(async (): Promise<boolean> => {
    if (!cliSessionId) return false;
    try {
      const token = localStorage.getItem("cortex_access_token") || "";
      const res = await fetch("/api/auth/cli/session/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ session_id: cliSessionId }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }, [cliSessionId]);

  // Runs only from the confirm screen's button — never automatically. A
  // signed-in user opening a crafted ?session= / ?callback= link must not
  // hand their session to whoever made it.
  const confirmCliSignIn = useCallback(async () => {
    setCliConfirmPending(false);
    if (cliSessionId) {
      const ok = await approveCLISession();
      if (!ok) {
        setError("This sign-in request has expired or was already used. Run the command again.");
        return;
      }
      if (sessionSource === "preview") {
        const redirectURL = safePreviewRedirect(searchParams.get("redirect"));
        if (redirectURL) {
          window.location.href = redirectURL;
          return;
        }
      }
      setCliApproved(true);
      return;
    }
    if (callbackUrl) sendToCallback();
  }, [cliSessionId, callbackUrl, approveCLISession, sendToCallback, sessionSource, searchParams]);

  const navigateAfterLogin = useCallback(() => {
    if (cliSessionId || callbackUrl) {
      // Ask first (confirmCliSignIn does the approving).
      setCliConfirmPending(true);
      return;
    }
    // Return to the page the user was trying to access before login
    const returnTo = safeReturnTo(searchParams.get("returnTo"));
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (surface.isDedicatedSurface && surface.appSlug) {
      navigate(`/${surface.appSlug}`, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [surface, navigate, callbackUrl, cliSessionId, searchParams]);

  useEffect(() => {
    if (isAuthenticated) navigateAfterLogin();
  }, [isAuthenticated, navigateAfterLogin]);

  /**
   * Leave for Atlas.
   *
   * `return_to` is this page's own URL, so whatever brought the user here —
   * a `?session=` from `cortex login`, a `?returnTo=` from ProtectedRoute, a
   * `?surface=` override — is still in the address bar when they come back,
   * and the CLI approval flow picks up exactly where it left off. The API
   * drops anything that isn't a same-origin path.
   */
  const startAtlasSignIn = () => {
    const here = new URL(window.location.href);
    // Don't carry a previous failure's banner back around the loop.
    here.searchParams.delete("error");
    here.searchParams.delete("ref");
    const returnTo = here.pathname + (here.search || "");
    window.location.href = `/api/auth/atlas/start?return_to=${encodeURIComponent(returnTo)}`;
  };

  // CLI login approved — success screen
  if (cliApproved) {
    return (
      <div className="min-h-screen flex bg-white">
        <BrandPanel variant={sessionSource === "preview" ? "preview" : "studio"} copy={copy} />
        <div className="flex-1 flex items-center justify-center px-6 relative bg-slate-50/80">
          <div className="w-full max-w-[400px] bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-5 mx-auto">
              <Terminal className="size-6 text-emerald-600" />
            </div>
            <h2 className="text-[20px] font-semibold text-slate-800 mb-2">You're all set</h2>
            <p className="text-[13px] text-slate-500 leading-relaxed">
              Authentication complete. You can close this tab and return to the terminal.
            </p>
            <p className="text-[11px] text-slate-400 mt-4">Your session is stored locally.</p>
          </div>
        </div>
      </div>
    );
  }

  // CLI / preview sign-in request that couldn't be approved (expired, used).
  if (isAuthenticated && (cliSessionId || callbackUrl) && error && !cliConfirmPending && !cliApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50/80">
        <div className="w-full max-w-[400px] bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <AlertCircle className="size-8 text-amber-500 mx-auto mb-4" />
          <p className="text-[14px] text-slate-700 leading-relaxed">{error}</p>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 h-10 px-5 rounded-lg border border-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-50"
          >
            Back to {copy.label}
          </button>
        </div>
      </div>
    );
  }

  // CLI / preview sign-in request — explicit approval
  if (cliConfirmPending && isAuthenticated && !cliApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50/80">
        <div className="w-full max-w-[400px] bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-5">
            <Terminal className="size-6 text-indigo-500" />
          </div>
          {copy === SIGN_IN_COPY.cortex ? (
            <>
              <h2 className="text-[19px] font-semibold text-slate-800 mb-2">
                Sign in to the Cortex {sessionSource === "preview" ? "preview" : "CLI"} on this computer?
              </h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                This gives the {sessionSource === "preview" ? "local preview server" : "command line tool"} full access to your Cortex account.
              </p>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-3 font-medium">
                Only approve if you just ran <code className="text-[12px] bg-slate-100 rounded px-1">cortex login</code> or
                started <code className="text-[12px] bg-slate-100 rounded px-1">cortex preview</code> yourself. If someone sent you this link, cancel.
              </p>
            </>
          ) : (
            // Docs/Enable never name Cortex; the request is the same one.
            <>
              <h2 className="text-[19px] font-semibold text-slate-800 mb-2">
                Sign in to the {sessionSource === "preview" ? "local preview server" : "command line tool"} on this computer?
              </h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                This gives it full access to your {copy.product} account.
              </p>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-3 font-medium">
                Only approve if you started this sign-in yourself on this computer. If someone sent you this link, cancel.
              </p>
            </>
          )}
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => { setCliConfirmPending(false); navigate("/", { replace: true }); }}
              className="flex-1 h-10 rounded-lg border border-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmCliSignIn}
              className="flex-1 h-10 rounded-lg bg-indigo-600 text-[13px] font-medium text-white hover:bg-indigo-700"
            >
              Approve sign-in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authLoading || isAuthenticated) {
    return <Spinner label="Signing in..." />;
  }

  // Preview mode: wait for preview config before showing login
  if (isLocalPreview && !previewConfigLoaded) {
    return <Spinner label="Loading..." />;
  }

  // Preview mode login — proxy through preview backend which handles everything
  if (isLocalPreview && !callbackUrl && !cliSessionId) {
    const handlePreviewSignIn = () => {
      // Preview server intercepts this route, creates session, starts polling,
      // and redirects the browser to production login with ?session=
      window.location.href = "/preview/auth/login";
    };
    return (
      <div className="min-h-screen flex bg-white">
        <BrandPanel variant="preview" />
        <div className="flex-1 flex items-center justify-center px-6 relative bg-slate-50/80">
          <div className="w-full max-w-[360px]">
            <div className="flex items-center gap-2.5 mb-8 lg:hidden justify-center">
              <img src="/cortex-logo.png" alt="" className="h-10 rounded-xl object-cover shadow-sm" />
              <span className="text-[26px] font-semibold tracking-[0.02em] text-slate-800 select-none">Cortex</span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
              <h1 className="text-[20px] font-semibold text-slate-800 mb-1">
                <span className="text-emerald-600">Preview</span> Server
              </h1>
              <p className="text-[13px] text-slate-400 mb-6">Sign in to access your workspace</p>
              <button
                onClick={handlePreviewSignIn}
                className="w-full px-5 py-3 text-[14px] font-medium rounded-xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md hover:border-slate-300 active:scale-[0.98] transition-all cursor-pointer"
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Sign in. One door: Atlas Identity. ──
  return (
    <div className="min-h-screen flex bg-white">
      <BrandPanel variant={cliSessionId && sessionSource === "preview" ? "preview" : brandVariant} copy={copy} />

      <div className="flex-1 flex items-center justify-center px-6 relative bg-slate-50/80">
        <div className="w-full max-w-[360px]">
          <div className="flex items-center gap-2.5 mb-8 lg:hidden justify-center">
            <img src={copy.logo} alt="" className={copy.mobileLogoClass} />
            <span className="text-[26px] font-semibold tracking-[0.02em] text-slate-800 select-none">{copy.label}</span>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h1 className="text-[20px] font-semibold text-slate-800 mb-1 text-center">
              Sign in to {copy.product}
            </h1>
            <p className="text-[13px] text-slate-400 mb-6 text-center">
              {copy.subtitle}
            </p>

            {error && (
              <div
                role="alert"
                className="mb-5 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3"
              >
                <AlertCircle className="size-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[12px] text-amber-900 leading-relaxed">{error}</p>
              </div>
            )}

            <button
              onClick={startAtlasSignIn}
              className="w-full h-11 rounded-xl bg-indigo-600 text-[14px] font-medium text-white shadow-sm hover:bg-indigo-700 active:scale-[0.98] transition-all cursor-pointer"
            >
              {copy.button}
            </button>

            <p className="text-[11px] text-slate-400 mt-5 text-center leading-relaxed">
              {copy.note}
            </p>
          </div>

          <p className="text-[11px] text-slate-400 mt-6 text-center">
            {copy.footer}
          </p>
        </div>
      </div>
    </div>
  );
}
